import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import Map from './Map';
import SearchForm from './SearchForm';
import CurrentConditions from './CurrentConditions';
import Forecast from './Forecast';
import HourlyBar from './HourlyBar';
import axios from 'axios';
import { getFusedAqi } from './services/aqiFusion';

function App() {
  // Start with USA overview - no specific location
  const [coordinates, setCoordinates] = useState({ lat: 39.8283, lon: -98.5795 });
  const [locationName, setLocationName] = useState(null);
  const [weatherData, setWeatherData] = useState(null);
  const [hourlyData, setHourlyData] = useState(null);
  const [aqi, setAqi] = useState(null);
  const [aqiLoading, setAqiLoading] = useState(false);
  const [detailedWeather, setDetailedWeather] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasSelectedLocation, setHasSelectedLocation] = useState(false);
  const [visibility, setVisibility] = useState(null);
  const [gridpointData, setGridpointData] = useState(null);
  const lastFetchedCoords = useRef(null);

  const handleLocationSelected = useCallback(async (lat, lon, displayName) => {
    let finalName = displayName;
    if (displayName === "Selected Location") {
      try {
        const response = await axios.get(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`);
        const addr = response.data.address;
        const city = addr.city || addr.town || addr.village || addr.hamlet;
        const state = addr.state;
        finalName = city ? `${city}${state ? ', ' + state : ''}` : response.data.display_name;
      } catch (err) {
        finalName = `${lat.toFixed(2)}, ${lon.toFixed(2)}`;
      }
    }
    setCoordinates({ lat, lon });
    setLocationName(finalName);
    setHasSelectedLocation(true);
  }, []);

  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          handleLocationSelected(latitude, longitude, "Selected Location");
        },
        (error) => {
          console.warn("Location access denied. Showing USA overview.");
        }
      );
    }
  }, [handleLocationSelected]);

  const fetchWeather = useCallback(async (lat, lon) => {
    setLoading(true);
    setAqiLoading(true);
    try {
      // 1. NWS Weather - Forecast, Hourly, and Gridpoint (for QPF)
      const pointsRes = await axios.get(`https://api.weather.gov/points/${parseFloat(lat).toFixed(4)},${parseFloat(lon).toFixed(4)}`);
      const forecastRes = await axios.get(pointsRes.data.properties.forecast);
      setWeatherData(forecastRes.data.properties.periods);

      const hourlyRes = await axios.get(pointsRes.data.properties.forecastHourly);
      setHourlyData(hourlyRes.data.properties.periods.slice(0, 24));
      
      // Fetch gridpoint data for quantitative precipitation forecast
      const gridpointUrl = pointsRes.data.properties.forecastGridData;
      const gridpointRes = await axios.get(gridpointUrl);
      setGridpointData(gridpointRes.data.properties);

      const detailedRes = await axios.get(
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,apparent_temperature,precipitation,weather_code,pressure_msl,wind_speed_10m,wind_direction_10m,wind_gusts_10m,uv_index&hourly=precipitation,snowfall&daily=sunrise,sunset,uv_index_max&timezone=auto&temperature_unit=fahrenheit&wind_speed_unit=mph&precipitation_unit=inch&past_hours=24&forecast_days=3`
      );
      setDetailedWeather(detailedRes.data);

      // Core weather data is ready; let the section render while the AQI call finishes.
      setLoading(false);

      // 2. Fused AQI (slow external dependency) loads independently so the rest of the app stays usable.
      try {
        const fused = await getFusedAqi(lat, lon);
        setAqi(fused.aqi);
      } catch (aqiError) {
        console.warn('AQI fetch failed:', aqiError);
        setAqi(null);
      } finally {
        setAqiLoading(false);
      }

      // 3. NWS Observation Station for Visibility only
      try {
        const stationsRes = await axios.get(pointsRes.data.properties.observationStations);
        if (stationsRes.data.features && stationsRes.data.features.length > 0) {
          const stationId = stationsRes.data.features[0].properties.stationIdentifier;
          const obsRes = await axios.get(`https://api.weather.gov/stations/${stationId}/observations/latest`);
          const visibilityMeters = obsRes.data.properties.visibility?.value;
          if (visibilityMeters !== null && visibilityMeters !== undefined) {
            setVisibility((visibilityMeters * 0.000621371).toFixed(1));
          } else {
            setVisibility(null);
          }
        }
      } catch (visError) {
        console.warn('Could not fetch visibility from NWS:', visError);
        setVisibility(null);
      }

    } catch (error) {
      console.error('Fetch Error:', error);
      setWeatherData([]); 
      setHourlyData([]);
      setVisibility(null);
      setGridpointData(null);
      setLoading(false);
      setAqiLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only fetch weather data if a location has been selected and coordinates have changed
    const coordsKey = `${coordinates.lat},${coordinates.lon}`;
    const lastCoordsKey = lastFetchedCoords.current;
    
    if (hasSelectedLocation && coordsKey !== lastCoordsKey) {
      lastFetchedCoords.current = coordsKey;
      fetchWeather(coordinates.lat, coordinates.lon);
    }
  }, [coordinates, fetchWeather, hasSelectedLocation]);


  return (
    <div className="app-container">
      <aside className="sidebar">
        <SearchForm onLocationSelected={handleLocationSelected} loading={loading} />
        <CurrentConditions 
          weatherData={weatherData} 
          locationName={locationName} 
          coordinates={coordinates}
          aqi={aqi}
          aqiLoading={aqiLoading}
          detailedWeather={detailedWeather}
          hourlyData={hourlyData}
          visibility={visibility}
          loading={loading}
          gridpointData={gridpointData}
        />
      </aside>
      <main className="main-content">
        <div className="map-wrapper">
          <Map 
            coordinates={coordinates} 
            weatherData={weatherData} 
            locationName={locationName} 
            onMapClick={handleLocationSelected}
            aqi={aqi}
            hasSelectedLocation={hasSelectedLocation}
          />
        </div>
        <HourlyBar 
          hourlyData={hourlyData} 
          daily={detailedWeather?.daily}
        />
      </main>
      <aside className="forecast-sidebar">
        <Forecast weatherData={weatherData} />
      </aside>
    </div>
  );
}

export default App;